// Shared upload primitives for the admin SPA.
//
// Two layers:
//  - putToPresignedUrl: the raw "PUT a file at an S3 presigned URL" step,
//    reused by every uploader (shoot images, RAWs, library photos).
//  - uploadLibraryPhoto: presign -> PUT for admin-owned "library" photos
//    (person avatars + location photos), which share one backend route.
//
// The shoot-image / RAW upload flow stays in PortfolioView: it is tightly
// coupled to per-image rows, progress, and the matcher, and abstracting it
// here would obscure more than it shares.

type ApiFetch = (path: string, options?: RequestInit) => Promise<Response>

/** PUT a file at a presigned S3 URL. Returns true on success. */
export async function putToPresignedUrl(uploadUrl: string, file: Blob, contentType?: string): Promise<boolean> {
  const headers: Record<string, string> = {}
  if (contentType) headers['Content-Type'] = contentType
  const put = await fetch(uploadUrl, { method: 'PUT', headers, body: file })
  return put.ok
}

/**
 * Presign -> PUT an admin "library" photo (person avatar or location photo).
 * `kind` selects the S3 prefix; `refId` scopes it to the entity. Returns the
 * stored S3 key, or null on any failure (with a user-facing alert).
 */
export async function uploadLibraryPhoto(
  apiFetch: ApiFetch,
  file: File,
  kind: 'person' | 'location' | 'client',
  refId: number,
): Promise<string | null> {
  const contentType = file.type || 'image/jpeg'
  const res = await apiFetch('/admin/portfolio/photo-upload-url', {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, contentType, kind, refId }),
  })
  if (!res.ok) {
    let msg = 'Could not get an upload URL'
    try { msg = (await res.json()).error || msg } catch { /* keep default */ }
    alert(msg)
    return null
  }
  const { uploadUrl, s3Key } = await res.json()
  const ok = await putToPresignedUrl(uploadUrl, file, contentType)
  if (!ok) { alert('Upload failed'); return null }
  return s3Key
}
